import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, styled } from '@mui/material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '60px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputBase-input': {
    color: 'white',
    textAlign: 'center',
    fontSize: '1.5rem',
    padding: '10px',
  },
}));

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({ value, onChange, disabled = false }) => {
  const [otpValues, setOtpValues] = useState<string[]>(value.split('').concat(Array(4 - value.length).fill('')));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Update internal state when external value changes
  useEffect(() => {
    setOtpValues(value.split('').concat(Array(4 - value.length).fill('')));
  }, [value]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Only allow digits
    if (newValue && !/^\d+$/.test(newValue)) {
      return;
    }

    // Take only the last character if multiple characters are pasted
    const digit = newValue.slice(-1);
    
    // Update the OTP array
    const newOtpValues = [...otpValues];
    newOtpValues[index] = digit;
    setOtpValues(newOtpValues);
    
    // Call the onChange callback with the new combined value
    onChange(newOtpValues.join(''));
    
    // Move to next input if a digit was entered
    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Move to next input on right arrow key
    if (e.key === 'ArrowRight' && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Move to previous input on left arrow key
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    // Only allow digits
    const digits = pastedData.replace(/\D/g, '').slice(0, 4);
    
    if (digits) {
      // Fill the OTP boxes with pasted digits
      const newOtpValues = [...Array(4).fill('')];
      digits.split('').forEach((digit, index) => {
        if (index < 4) newOtpValues[index] = digit;
      });
      
      setOtpValues(newOtpValues);
      onChange(newOtpValues.join(''));
      
      // Focus the next empty input or the last one if all are filled
      const nextEmptyIndex = newOtpValues.findIndex(val => !val);
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[3]?.focus();
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', my: 2 }}>
      {[0, 1, 2, 3].map((index) => (
        <StyledTextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={otpValues[index]}
          onChange={(e) => handleChange(index, e as React.ChangeEvent<HTMLInputElement>)}
          onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            style: { textAlign: 'center' }
          }}
          disabled={disabled}
          variant="outlined"
          autoComplete="off"
        />
      ))}
    </Box>
  );
};

export default OtpInput;
